import { ART_SHAPES, toWorld } from "./artShapes.js";
import { shapeContains, shapeOutline, DEFAULT_DOOR_SHAPE } from "./doorShape.js";
import { pointInPolygon, polygonBounds } from "./geometry.js";
/**
 * Los objetos que pueblan una sala.
 *
 * La regla que sostiene el diseño es la misma que ya rige las salas: **el objeto es
 * un DATO, el comportamiento es una CLASE**. Una banca en la cancha no es un tipo
 * distinto de una banca en el bosque — es la misma clase con otro `rect`. Lo que se
 * hereda es el tipo, nunca la instancia.
 *
 * De ahí sale la división de este archivo: aquí vive todo lo que cliente y servidor
 * necesitan saber para dibujar, medir y decidir si algo responde al clic; el efecto
 * de tocarlo vive en las clases del servidor, porque es autoridad suya.
 *
 * El import de `RoomId` es SÓLO de tipo, y eso no es un detalle: `rooms.ts` va a
 * importar de aquí en cuanto las puertas migren, y un import de valor cerraría un
 * ciclo en tiempo de ejecución. Los tipos se borran al compilar; los valores no.
 */
export const ELEMENT_KINDS = [
    "puerta",
    "silla",
    "mesa",
    "arbol",
    "mostrador",
    "juego",
    "cancha",
    "directorio",
];
export function isElementKind(value) {
    return typeof value === "string" && ELEMENT_KINDS.includes(value);
}
/** Los dos lados. Ausente en un elemento significa "que lo decida su borde inferior". */
export const ELEMENT_LAYERS = ["back", "front"];
export function isElementLayer(value) {
    return typeof value === "string" && ELEMENT_LAYERS.includes(value);
}
/**
 * La clave de arte de un elemento: la suya, o la de su tipo.
 *
 * En un solo sitio para que el dibujo, el cargador y la validación no puedan
 * discrepar sobre cuál es el respaldo.
 */
export function artKeyOf(element) {
    return element.art ?? element.kind;
}
export const ELEMENT_TRAITS = {
    /*
     * La puerta bloquea: se llega hasta ella y se cruza desde el lado, igual que
     * cualquier otro objeto. Un pasadizo que se atraviesa caminando declara
     * `solid: false` en su instancia en vez de ser otro tipo.
     */
    puerta: { solid: true, interactive: true, label: "Puerta" },
    /*
     * Una silla ESTORBA, y además te sientas en ella.
     *
     * Fue `solid: false` un tiempo, para poder caminar por encima hasta el asiento. Se
     * cambió porque una silla que no estorba no se lee como un mueble: el avatar la
     * atraviesa y la escena deja de comportarse como un sitio.
     *
     * Los dos motivos que tenía aquel comentario para no hacerlo ya no aplican, y no por
     * descuido — los cubren los datos:
     *
     * · "Dejaba atrapado a quien se sentaba lejos de su arista de salida": las diecinueve
     *   superficies del campus declaran `fallsAtEdge: true`, así que de una silla siempre
     *   se puede bajar caminando, haya arista de acceso o no.
     * · "Obligaba a acercarse por fuera y aparecer sentado de un salto": eso es justo lo
     *   que ya pasaba. Las diecinueve declaran `accessEdges: []` —no se sube caminando,
     *   sólo con un clic— así que el salto al asiento existía igual; lo único que cambia
     *   es que ahora se camina hasta el costado en vez de atravesar el mueble.
     *
     * Si alguna vez se quiere una silla que sí se pise —una grada, un banco corrido— es
     * `solid: false` en su instancia, sin tocar el tipo.
     */
    silla: { solid: true, interactive: true, label: "Silla" },
    /*
     * Una mesa es un OBSTÁCULO, y nada más: se rodea.
     *
     * No se sube uno encima ni responde al clic. El mecanismo de superficies existe y
     * una mesa concreta podría usarlo —basta declararle una en el catálogo— pero
     * ponérsela por defecto convertía cada mesa del campus en algo a lo que treparse, y
     * eso no es lo que una mesa hace en este juego.
     */
    mesa: { solid: true, interactive: false, label: "Mesa" },
    arbol: { solid: true, interactive: false, label: "Árbol" },
    /*
     * El mostrador bloquea y se atiende desde delante, como un mostrador de verdad.
     *
     * Que sea sólido no es decoración: es lo que obliga a rodearlo para llegar al lado
     * del cliente, y con `anchor` en el catálogo se decide cuál es ese lado. Sin eso, el
     * jugador podría acabar comprando desde dentro de la caja.
     */
    mostrador: { solid: true, interactive: true, label: "Mostrador" },
    /* Un puesto de juego se toca; estorbar es lo que hace que se le camine alrededor. */
    juego: { solid: true, interactive: true, label: "Puesto de juego" },
    /* La cancha se toca desde fuera, como un puesto: lo que estorba es la mesa donde uno
       se apunta, no el piso de juego, que sigue siendo parte de la sala. */
    cancha: { solid: true, interactive: true, label: "Cancha de volley" },
    /*
     * El directorio: se toca para abrir la lista, y estorba como el tablón que es.
     *
     * Sólido, al contrario que una puerta, porque no se cruza caminando: se llega hasta él
     * y se lee. Atravesarlo sería atravesar una pared.
     */
    directorio: { solid: true, interactive: true, label: "Directorio de clubes" },
};
/**
 * ¿Bloquea el paso? La instancia manda sobre el tipo.
 *
 * Existe como función y no como lectura suelta de `solid` para que nadie se salte el
 * respaldo: leer `element.solid` directamente da `undefined` en la mayoría de los
 * objetos, y `undefined` es falso, así que todo dejaría de estorbar en silencio.
 */
export function isSolid(element) {
    return element.solid ?? ELEMENT_TRAITS[element.kind].solid;
}
// -------------------------------------------------------------------- geometría
/**
 * El contorno del objeto, en coordenadas de mundo.
 *
 * Sale de `shapeOutline`, la misma función que usan las puertas. Que dibujo, clic y
 * validación salgan todos de aquí es lo que garantiza que lo que se ve es lo que se
 * puede tocar; un hit test con fórmula propia acabaría separándose.
 */
export function elementOutline(element) {
    return shapeOutline(element.rect, element.shape ?? DEFAULT_DOOR_SHAPE, element.points);
}
export function elementContains(element, point) {
    return shapeContains(element.rect, element.shape, element.points, point);
}
/**
 * El contorno que ESTORBA, que no tiene por qué ser el que se dibuja.
 *
 * La diferencia aparece en cuanto un objeto tiene sprite: lo dibujado es la imagen, y
 * la imagen puede dejar media caja vacía. `elementOutline` describe la silueta
 * declarada —la que se pinta cuando no hay arte, y la zona que se pisa en una puerta—;
 * esto describe dónde no se puede estar. Todo lo que pregunte "¿cabe el jugador aquí?"
 * usa ésta; el dibujo y el clic siguen con la otra.
 *
 * Tres fuentes, de la más específica a la más general:
 *
 *  1. `element.collision` — lo que decida esta instancia.
 *  2. `ART_SHAPES[clave].points` — lo que se moldeó para ese dibujo, que es el caso
 *     normal y lo que hace que dos mesones iguales estorben igual.
 *  3. `elementOutline(element)` — la silueta declarada.
 *
 * Que el respaldo sea la silueta y no la caja no es un detalle: una puerta en rombo
 * tiene que seguir bloqueando en rombo, y sin este orden todo lo que hoy usa
 * `shape`/`points` habría empezado a chocar como un rectángulo sin que nada avisara.
 */
export function collisionOutline(element) {
    if (element.collision && element.collision.length >= 3) {
        return toWorld(element.collision, element.rect);
    }
    /* Uno pintado en el fondo no usa la silueta de su clave de arte: ese dibujo no se
       pinta, y la forma de una silla no dice nada de unas gradas que sólo comparten tipo. */
    const shape = element.backdrop ? undefined : ART_SHAPES[artKeyOf(element)];
    if (shape?.points && shape.points.length >= 3) {
        return toWorld(shape.points, element.rect);
    }
    return elementOutline(element);
}
/** ¿Este punto cae dentro de lo que estorba? */
export function collisionContains(element, point) {
    return pointInPolygon(collisionOutline(element), point);
}
/** El centro de su caja. Mismo criterio que las puertas, por las mismas razones. */
/**
 * La profundidad de dibujo de cada objeto de una sala.
 *
 * El ORDEN lo decide la pila —la posición en `room.elements`, que es lo que reordena
 * el editor— y los VALORES son los bordes inferiores que ya había, repartidos de menor
 * a mayor. Las dos mitades importan:
 *
 * · Que mande la pila es lo que permite superponer: dos muebles se tapan en el orden
 *   que tú digas, no en el que salga de su posición en el suelo.
 * · Que los valores no cambien es lo que deja al jugador fuera de esto. Él se ordena
 *   por su Y contra el mismo conjunto de números de siempre, así que reordenar la pila
 *   cambia quién tapa a quién entre objetos y no toca con quién se cruza el jugador.
 *
 * Por eso la migración ordenó cada sala por borde inferior: con la pila así, cada
 * objeto recibe su propio valor y el campus se ve exactamente igual que antes.
 */
/**
 * Delante de todo lo que camina. Por debajo de los rótulos (90.000) y del editor
 * (100.000): una copa de árbol puede taparte a ti, pero tapar quién eres confunde más
 * de lo que ayuda.
 */
export const LAYER_IN_FRONT = 80_000;
/**
 * Detrás de todo lo que camina, y por encima del fondo y de las marcas del suelo (0 y 1).
 *
 * No es cero: una alfombra tiene que taparle la marca de clic al piso, no al revés.
 */
export const LAYER_BEHIND = 2;
/**
 * Qué tapa a qué.
 *
 * Lo normal —y lo que hace que caminar por detrás de un árbol funcione sin que nadie lo
 * programe— es que la profundidad de un objeto sea su BORDE INFERIOR, el sitio donde
 * apoya, en la misma recta numérica que la Y de los pies del jugador. Acá se reparten
 * esos bordes ORDENADOS y por posición en el array: así el orden de la lista es lo que
 * manda, pero los números que reparte siguen siendo bordes reales y siguen midiéndose
 * contra los avatares.
 *
 * Lo que se salió de esa regla lleva `layer` y se va a una banda fija. Esos NO entran en
 * el reparto: si entraran, consumirían un borde del montón y correrían la profundidad de
 * todos los demás un puesto — un objeto fijado al frente cambiaría quién tapa a quién
 * entre los que no tienen nada que ver con él.
 */
export function elementDepths(elements) {
    const automaticos = elements.filter((element) => element.layer === undefined);
    const bases = automaticos
        .map((element) => element.rect.y + element.rect.height)
        .sort((a, b) => a - b);
    const depths = new Map();
    let siguiente = 0;
    for (const element of elements) {
        if (element.layer === "front")
            depths.set(element.id, LAYER_IN_FRONT);
        else if (element.layer === "back")
            depths.set(element.id, LAYER_BEHIND);
        else
            depths.set(element.id, bases[siguiente++]);
    }
    return depths;
}
export function elementCenter(element) {
    return {
        x: element.rect.x + element.rect.width / 2,
        y: element.rect.y + element.rect.height / 2,
    };
}
/** ¿Este punto cae sobre la superficie elevada del objeto? */
export function onSurface(element, point) {
    const surface = element.surface;
    return surface !== undefined && pointInPolygon(surface.polygon, point);
}
/**
 * Las aristas por las que se entra y se sale, como pares de puntos.
 *
 * Devolver los segmentos y no los índices es lo que evita que cada consumidor repita
 * el `(i + 1) % n` — y ese módulo olvidado, que deja la última arista fuera, es un
 * fallo que no se ve hasta que alguien intenta subirse justo por ahí.
 */
export function accessEdges(element) {
    const surface = element.surface;
    if (!surface)
        return [];
    const polygon = surface.polygon;
    const wanted = surface.accessEdges ?? polygon.map((_, index) => index);
    const edges = [];
    for (const index of wanted) {
        const from = polygon[index];
        const to = polygon[(index + 1) % polygon.length];
        if (from && to)
            edges.push([from, to]);
    }
    return edges;
}
/** La caja de una superficie, para ordenar el dibujo sin recorrer sus vértices. */
export function surfaceBounds(element) {
    return element.surface ? polygonBounds(element.surface.polygon) : null;
}
//# sourceMappingURL=elements.js.map